import React, { Fragment, useEffect, useLayoutEffect, useState } from "react";
import Icon from "../Icon/Icon";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import BottomNavBar from "./BottomNavBar";
import Header from "./Header";
import "./Layout.scss"
import { useDispatch, useSelector } from "react-redux";
import CreateModal from "../CreateModal/CreateModal";
import { usePathLocation } from "../../../helpers/hooks";
import GetApp from "../GetApp/GetApp";
import GetAppBanner from "../GetApp/GetAppBanner";
import { GET_APP_BANNER_VERSION } from "../../constants/globals";
import { getUserOnboardingVersion } from "../../Reducers/actions/authActions";
import { changeOnboardingVersion } from "../../Reducers/actions/userActions";
import { openShippingAddressForm } from "../../Reducers/actions/shippingAddressActions";
import { useSoftwall } from "../../containers/SoftWall/SoftWall";
import { openShippingMethodForm } from "../../Reducers/actions/shippingMethodsActions";
import { getKycStatus } from "../../Reducers/actions/accountActions";
import useSocialNetworksPrompt from "../../hooks/useSocialNetworksPrompt";
import SocialNetworksPrompt from "../SocialNetworksPrompt/SocialNetworksPrompt";
import useGoogleContacts from "../../hooks/useGoogleContacts";
import { handleGoBackRedirection } from "./navigationHelpers.jsx";
import { isPublicProfilePathname } from "../../../helpers/profilePaths";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
const ROOT_TABS = ["/explore", "/news", "/search", "/search/news", "/search/library", "/create", "/library", '/profile',];
const NOARROW_TABS = ["/profile"];
const SHOW_MENU_NO_ROOT = ["/profile",]
const EXCLUDE = ["/chat", "/creation-detail", "/creations/add", "/edit-profile", "/complete-profile", "/add-social-links"]
const EXCLUDE_BOTTOM_NAV = ["/shipments", "/orders", "/invite-community", "/earnings", "/creator-analytics", "/on-boarding", "/shipping-addresses"]

const getPageTitle = (pathname) => {
    const path = pathname.split('/')[1];
    return path.charAt(0).toUpperCase() + path.slice(1);
};

function isExcludedPath(pathname) {
    if (isPublicProfilePathname(pathname)) return true;
    // Exact matches
    const EXCLUDE = [
        "/chat", "/creations/add", "/change-password", "/privacy-settings", "/complete-profile", "/add-social-links", "/profile", "/on-boarding",
    ];
    // Pattern matches
    const EXCLUDE_PATTERNS = [
        /^\/news\/[^/]+$/, // matches /news/<id>
        /^\/news\/[^/]+\/.+$/, // matches /news/<id>/<sth>...
        /^\/creations\/[^/]+\/edit$/, // matches /creations/<id>/edit
        /^\/creations\/[^/]+$/, // matches /creations/<id>
        /^\/profiles\/[^/]+$/, // matches /profiles/<id>
        /^\/contribute\/[^/]+$/, // matches /contribute/<username>
    ];



    if (EXCLUDE.includes(pathname)) return true;
    return EXCLUDE_PATTERNS.some(pattern => pattern.test(pathname));
}

export { handleGoBackRedirection } from "./navigationHelpers.jsx";

const Layout = () => {
    const isLogged = useSelector((state) => state.auth?.auth?.isLogged)
    const userRole = useSelector((state) => state.auth?.user?.role)
    const hideBottomNav = useSelector((state) => state.navigation?.hideBottomNav)
    const GetAppBannerVersion = useSelector((state) => state.auth?.onboarding_version)

    const handleUpgradeBoardingVersion = () => {
        if (isLogged) {
            changeOnboardingVersion().then(({ success }) => {
                if (success) {
                    setShowBanner(false)
                    setOpenGetAppModal(false)
                }


            })
        } else {
            setShowBanner(false)
            setOpenGetAppModal(false)
        }
    }
    const navigate = useNavigate()
    const location = useLocation()
    const [previousPath, setPreviousPath] = useState(null)
    const [showCreateModal, setShowCreateModal] = useState(false)

    const [showBanner, setShowBanner] = useState(isLogged)
    const [openGetAppModal, setOpenGetAppModal] = useState(false)
    const [isImportingContacts, setIsImportingContacts] = useState(false)
    const [kycStatus, setKycStatus] = useState(null);
    const [kycBannerLoading, setKycBannerLoading] = useState(false);
    const [kycBannerDismissed, setKycBannerDismissed] = useState(false);

    // Google Contacts hook for import functionality
    const {
        isLoading: isGoogleContactsLoading,
        importGoogleContacts
    } = useGoogleContacts(GOOGLE_CLIENT_ID);

    const dispatch = useDispatch()
    const sw = useSoftwall();
    usePathLocation();

    const { navbar } = useSelector((state) => ({
        navbar: state?.navbar
    }));

    // Social networks prompt modal (app opened trigger - once per day)
    const socialNetworksPrompt = useSocialNetworksPrompt('app-open');
    useEffect(() => {
        dispatch(getUserOnboardingVersion())
    }, [])
    useEffect(() => {
        setShowBanner(true)
    }, [isLogged])
    useEffect(() => {
        setKycBannerDismissed(false);
    }, [kycStatus?.has_light_kyc, kycStatus?.eventually_due?.length]);
    useEffect(() => {
        const shouldCheckKyc = isLogged && (userRole === "creator" || userRole === "admin");
        if (!shouldCheckKyc) {
            setKycStatus(null);
            return;
        }

        setKycBannerLoading(true);
        getKycStatus()
            .then((response) => {
                if (!response?.success) {
                    setKycStatus(null);
                    return;
                }
                setKycStatus(response.data || null);
            })
            .catch((error) => {
                console.error("Failed to fetch KYC status:", error);
                setKycStatus(null);
            })
            .finally(() => {
                setKycBannerLoading(false);
            });
    }, [isLogged, userRole, location.pathname]);
    useEffect(() => {
        setPreviousPath(location.pathname);
        // Preserve the active Account tab (Settings/Dashboard) while navigating within the
        // account area and its Dashboard sub-pages (incl. nested routes), so returning from a
        // sub-page restores the correct tab. Reset to the default (Settings) only when the user
        // leaves the area entirely.
        const accountAreaPrefixes = ['/my-profile', '/orders', '/withdrawals', '/earnings', '/creator-analytics', '/shipping-methods'];
        const inAccountArea = accountAreaPrefixes.some(
            (prefix) => location.pathname === prefix || location.pathname.startsWith(prefix + '/')
        );
        if (!inAccountArea) {
            dispatch({
                type: 'SET_ACTIVE_SETTINGS',
                payload: 0
            })
        }
    }, [location])

    const handleGoBack = (e) => {
        e.preventDefault()

        handleGoBackRedirection(navigate, previousPath);
    };

    const handleOpenCreateModal = () => {
        if (sw?.requireAuth && !sw.requireAuth('Create', 'Sign up or log in to publish a creation')) {
            return;
        }
        setShowCreateModal(true);
    };

    const handleCloseCreateModal = () => {
        setShowCreateModal(false);
    };

    // Only show back arrow if not on a root tab
    const showBackArrow = !ROOT_TABS.includes(location.pathname) && !NOARROW_TABS.includes(location.pathname)

    const exclude = isExcludedPath(location.pathname);


    const [isNavigatingToWithdrawal, setIsNavigatingToWithdrawal] = useState(false);

    const handleWithdrawalClick = () => {
        setIsNavigatingToWithdrawal(true);
        navigate("/withdrawals");
        setIsNavigatingToWithdrawal(false);

    };

    const handleImportContactsClick = async () => {
        if (!GOOGLE_CLIENT_ID) {
            alert('Google Contacts integration is not configured. Please contact support.');
            return;
        }

        setIsImportingContacts(true);
        try {
            const contacts = await importGoogleContacts();
            if (contacts && contacts.length > 0) {
                // Navigate to contact list screen with contacts in state
                navigate('/invite-contacts', { state: { contacts } });
            } else {
                alert('No contacts found. Please make sure you have contacts in your Google account.');
            }
        } catch (error) {
            // Handle user cancellation gracefully
            if (error.message?.includes('cancel') || 
                error.message?.includes('popup') || 
                error.message?.includes('user_closed')) {
                // User cancelled, don't show error
                return;
            }
            console.error('Error importing contacts:', error);
            alert(error.message || 'Failed to import contacts. Please try again.');
        } finally {
            setIsImportingContacts(false);
        }
    };

    const suppressGetAppBannerForGuestPayment =
        location.pathname === "/explore" &&
        new URLSearchParams(location.search).get("verifyEmailBanner") === "1";

    const suppressGetAppBannerForProfile =
        location.pathname === "/profile" || location.pathname.startsWith("/profile/");

    const shouldShowGetAppBanner =
        !suppressGetAppBannerForGuestPayment &&
        !suppressGetAppBannerForProfile &&
        (
        isLogged
            ? (GetAppBannerVersion != null && showBanner && GetAppBannerVersion < GET_APP_BANNER_VERSION)
            : showBanner
    );
    const hasEventuallyDue = Array.isArray(kycStatus?.eventually_due) && kycStatus.eventually_due.length > 0;
    const hasLightKyc = !!kycStatus?.has_light_kyc;
    const isKycBannerRoute = location.pathname === "/explore";
    const disableKycBanner = true;
    const showKycBanner =
        !disableKycBanner &&
        !kycBannerLoading &&
        isLogged &&
        (userRole === "creator" || userRole === "admin") &&
        isKycBannerRoute &&
        (!hasLightKyc || hasEventuallyDue) &&
        !kycBannerDismissed;
    const kycBannerMessage = !hasLightKyc
        ? "Set up your payout account to publish and receive payments."
        : "Complete Stripe verification to keep withdrawals available.";

    useEffect(() => {
        const shouldApplyGlobalOffsetFix = showKycBanner;
        document.body.classList.toggle(
            "has-global-kyc-banner",
            shouldApplyGlobalOffsetFix
        );

        return () => {
            document.body.classList.remove("has-global-kyc-banner");
        };
    }, [showKycBanner, location.pathname]);

    /** Fixed `.app-header-content` stacks GetAppBanner + 72px toolbar; scroll content must offset the full height. */
    const applyGetAppBannerStackOffset =
        shouldShowGetAppBanner && ROOT_TABS.includes(location.pathname);

    useEffect(() => {
        document.body.classList.toggle("has-get-app-banner", applyGetAppBannerStackOffset);
        return () => {
            document.body.classList.remove("has-get-app-banner");
        };
    }, [applyGetAppBannerStackOffset]);

    // Dynamically measure the fixed header stack height (GetAppBanner + header) and expose it as a CSS var.
    // This avoids page overlap when banner height changes due to padding/margins/responsive text.
    useLayoutEffect(() => {
        const updateOffset = () => {
            const headerStack = document.querySelector(".app-header-content");
            const height = headerStack?.getBoundingClientRect?.().height;
            if (typeof height === "number" && height > 0) {
                document.body.style.setProperty("--mobile-root-header-offset", `${Math.ceil(height)}px`);
            } else {
                document.body.style.removeProperty("--mobile-root-header-offset");
            }
        };

        updateOffset();

        const headerStack = document.querySelector(".app-header-content");
        let ro = null;
        if (headerStack && typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(() => updateOffset());
            ro.observe(headerStack);
        } else {
            window.addEventListener("resize", updateOffset, { passive: true });
        }

        return () => {
            ro?.disconnect?.();
            window.removeEventListener("resize", updateOffset);
            document.body.style.removeProperty("--mobile-root-header-offset");
        };
    }, [location.pathname, shouldShowGetAppBanner]);

    return (
        <Fragment>
            {openGetAppModal && <GetApp onClose={handleUpgradeBoardingVersion} />}
            {showCreateModal && <CreateModal onClose={handleCloseCreateModal} />}
            {socialNetworksPrompt.isOpen && (
                <SocialNetworksPrompt 
                    open={socialNetworksPrompt.isOpen} 
                    onClose={socialNetworksPrompt.closeModal} 
                />
            )}

            {ROOT_TABS.includes(location.pathname) ? (
                <Header title={getPageTitle(location.pathname)}
                    searchType={location.pathname === "/explore" ? "creations" : location.pathname === "/library" ? "library" : "news"}
                    showSearch={location.pathname !== "/profile"}
                    banner={shouldShowGetAppBanner}
                    onCloseBanner={() => handleUpgradeBoardingVersion()}
                    onOpenGetAppModal={() => setOpenGetAppModal(true)}
                    onOpenCreateModal={handleOpenCreateModal}
                />
            ) : (
                <>
                    {/* Desktop-only: persistent nav on non-root pages */}
                    <Header
                        title={navbar?.title || getPageTitle(location.pathname)}
                        showSearch={false}
                        banner={false}
                        onOpenCreateModal={handleOpenCreateModal}
                        desktopOnly
                    />
                    {/* Mobile-only: back-arrow nav bar */}
                    {!exclude && showBackArrow && window.history.length > 1 && (
                        <div className="nav-container d-flex jcc aic">
                            <div className="inner-container d-flex jcc aic">
                                <div className="nav-title">
                                    {navbar?.title || getPageTitle(location.pathname)}
                                </div>
                                {navbar?.showBackArrow &&
                                    <div className="nav-icon" onClick={handleGoBack}>
                                        <Icon alias={"chevron-left"} stroke={"#0A0F1E"} color="#0A0F1E" width={24} />
                                    </div>
                                }
                                {
                                    location.pathname.includes("/earnings") &&
                                    <button className="red-outline-btn fs-tiny fw-extra-bold" style={{ position: 'absolute', right: "8px" }} disabled={isNavigatingToWithdrawal} onClick={handleWithdrawalClick}>

                                        {isNavigatingToWithdrawal ? (
                                            <div className="d-flex f-row aic jcc">
                                                <span className="spinner-small mr-4"></span>
                                                Loading...
                                            </div>
                                        ) : (
                                            'Withdrawal'
                                        )}
                                    </button>
                                }
                                {
                                    (location.pathname.includes("/shipping-addresses") || location.pathname.includes("/shipping-methods")) &&
                                    <div
                                        className="bg-bg-grey icon-container mr-16 d-flex aic jcc c-pointer"
                                        style={{ position: 'absolute', right: "8px" }}
                                        onClick={() => {
                                            if (location.pathname.includes("/shipping-addresses")) {
                                                dispatch(openShippingAddressForm());
                                            } else if (location.pathname.includes("/shipping-methods")) {
                                                dispatch(openShippingMethodForm());
                                            }
                                        }}
                                    >
                                        <Icon alias={"plus_v2"} stroke="#FF3232" color='none' width={20} />
                                    </div>
                                }
                                {
                                    location.pathname.startsWith("/creator-analytics/creations/") &&
                                    <div
                                        className="bg-bg-grey icon-container mr-16 d-flex aic jcc c-pointer"
                                        style={{ position: 'absolute', right: "8px" }}
                                        onClick={() => {
                                            const creationId = location.pathname.split('/')[3];
                                            if (creationId) navigate(`/creations/${creationId}/edit`);
                                        }}
                                        aria-label="Edit creation"
                                    >
                                        <Icon alias={"edit_v2"} stroke="#FF3232" color="none" width={24} />
                                    </div>
                                }
                                {
                                    location.pathname.startsWith("/creator-analytics/news/") &&
                                    <div
                                        className="bg-bg-grey icon-container mr-16 d-flex aic jcc c-pointer"
                                        style={{ position: 'absolute', right: "8px" }}
                                        onClick={() => {
                                            const newsId = location.pathname.split('/')[3];
                                            if (newsId) navigate(`/news/${newsId}/edit`);
                                        }}
                                        aria-label="Edit news"
                                    >
                                        <Icon alias={"edit_v2"} stroke="#FF3232" color="none" width={24} />
                                    </div>
                                }
                                {
                                    location.pathname === "/invite-community" &&
                                    <div
                                        className=" icon-container mr-16 d-flex aic jcc c-pointer"
                                        style={{ position: 'absolute', right: "8px" }}
                                        onClick={handleImportContactsClick}
                                        title="Import Contacts"
                                    >
                                        {isImportingContacts || isGoogleContactsLoading ? (
                                            <span className="spinner-small"></span>
                                        ) : (
                                            <Icon alias={"contact_picker"} stroke="#FF3232" color="#FF3232" width={24} height={24} />
                                        )}
                                    </div>
                                }
                            </div>
                        </div>
                    )}
                </>
            )}
            <div className="layout-content-wrapper pb-40 mb-40">
                {showKycBanner && (
                    <div
                        className="kyc-banner"
                        role="status"
                        aria-live="polite"
                    >
                        <button
                            type="button"
                            className="kyc-banner-text"
                            onClick={() => navigate("/withdrawals")}
                            aria-label="Open withdrawals to continue verification"
                        >
                            {kycBannerMessage}
                        </button>
                        <button
                            type="button"
                            className="kyc-banner-close"
                            onClick={() => setKycBannerDismissed(true)}
                            aria-label="Dismiss verification banner"
                        >
                            ×
                        </button>
                    </div>
                )}
                <Outlet />
            </div>
            {((!exclude && !hideBottomNav && !EXCLUDE_BOTTOM_NAV.some((item) => location.pathname.startsWith(item))) || SHOW_MENU_NO_ROOT.includes(location.pathname) || location.pathname.startsWith('/profiles/') || isPublicProfilePathname(location.pathname)) && <BottomNavBar onOpenCreateModal={handleOpenCreateModal} />}
        </Fragment>
    )
}

export default Layout